/**
 * Semana Elixir — contador y rotador de la barra de anuncios.
 *
 * <cm-elixir-countdown>
 *   data-mode="deadline": cuenta hacia data-deadline ("AAAA-MM-DD HH:MM") en la
 *     zona horaria data-offset (minutos respecto a UTC, CDMX = -360). Todos los
 *     visitantes ven el mismo tiempo.
 *   data-mode="visitor": data-hours horas por visitante, guardado en localStorage
 *     bajo data-key. Al llegar a 00:00:00 arranca un ciclo nuevo.
 *   data-days="auto": muestra días solo si falta más de 24 h; "never" los suma a las horas.
 *   data-expire="hide": oculta el contador al terminar (solo deadline).
 *   Hijos: [data-u="d|h|m|s"] y, opcionales, [data-u-days] para el bloque de días.
 *
 * <cm-elixir-rotator>
 *   Rota [data-slide] cada data-interval ms. Los slides con data-slide="desktop"
 *   o "mobile" solo participan en su vista (corte 749px).
 */
(function () {
  if (!customElements.get('cm-elixir-countdown')) {
    class CmElixirCountdown extends HTMLElement {
      connectedCallback() {
        this.units = {};
        this.querySelectorAll('[data-u]').forEach((el) => {
          this.units[el.dataset.u] = el;
        });
        this.daysEls = this.querySelectorAll('[data-u-days]');
        this.mode = this.dataset.mode === 'visitor' ? 'visitor' : 'deadline';
        this.days = this.dataset.days || 'auto';

        if (this.mode === 'deadline') {
          this.target = this.parseDeadline(this.dataset.deadline, Number(this.dataset.offset) || 0);
          if (this.target === null) {
            console.warn('[cm-elixir] Fecha límite inválida. Usa el formato AAAA-MM-DD HH:MM');
          }
        } else {
          this.period = Math.max(1, parseFloat(this.dataset.hours) || 8) * 3600000;
          this.key = this.dataset.key || 'cm-elixir-timer';
          this.target = this.visitorEnd(Date.now());
        }

        this.tick = this.tick.bind(this);
        this.tick();
        this.timer = setInterval(this.tick, 1000);
      }

      disconnectedCallback() {
        clearInterval(this.timer);
      }

      // "2026-10-20 23:59" en la zona indicada -> timestamp UTC
      parseDeadline(str, offsetMin) {
        const m = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2}))?$/.exec((str || '').trim());
        if (!m) return null;
        const hasTime = m[4] !== undefined;
        const utc = Date.UTC(
          Number(m[1]), Number(m[2]) - 1, Number(m[3]),
          hasTime ? Number(m[4]) : 23,
          hasTime ? Number(m[5]) : 59,
          hasTime ? 0 : 59
        );
        return utc - offsetMin * 60000;
      }

      read() {
        try {
          return Number(window.localStorage.getItem(this.key)) || 0;
        } catch (e) {
          return this.memoryEnd || 0;
        }
      }

      write(end) {
        this.memoryEnd = end;
        try {
          window.localStorage.setItem(this.key, String(end));
        } catch (e) {
          /* modo privado: el ciclo vive solo en esta página */
        }
      }

      // Fin del ciclo actual del visitante; si ya venció, encadena ciclos nuevos
      visitorEnd(now) {
        let end = this.read();
        if (!end || end - now > this.period) {
          end = now + this.period;
        } else if (end <= now) {
          end += Math.ceil((now - end + 1) / this.period) * this.period;
        }
        this.write(end);
        return end;
      }

      tick() {
        const now = Date.now();
        let ms;

        if (this.mode === 'visitor') {
          if (this.target <= now) this.target = this.visitorEnd(now);
          ms = this.target - now;
        } else {
          ms = this.target === null ? 0 : this.target - now;
          if (ms <= 0) {
            ms = 0;
            clearInterval(this.timer);
            if (this.dataset.expire === 'hide') {
              this.hidden = true;
              return;
            }
          }
        }

        const total = Math.floor(ms / 1000);
        let d = Math.floor(total / 86400);
        let h = Math.floor((total % 86400) / 3600);
        const showDays = this.days !== 'never' && d > 0;
        if (!showDays) {
          h += d * 24;
          d = 0;
        }

        this.daysEls.forEach((el) => {
          el.hidden = !showDays;
        });
        this.set('d', d);
        this.set('h', h);
        this.set('m', Math.floor((total % 3600) / 60));
        this.set('s', total % 60);
      }

      set(unit, n) {
        const el = this.units[unit];
        if (!el) return;
        const v = String(n).padStart(2, '0');
        if (el.textContent !== v) el.textContent = v;
      }
    }

    customElements.define('cm-elixir-countdown', CmElixirCountdown);
  }

  if (!customElements.get('cm-elixir-rotator')) {
    class CmElixirRotator extends HTMLElement {
      connectedCallback() {
        this.mq = window.matchMedia('(max-width: 749px)');
        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.interval = Math.max(2000, Number(this.dataset.interval) || 4000);
        this.index = 0;
        this.paused = false;

        this.onChange = () => this.reset();
        this.onEnter = () => { this.paused = true; };
        this.onLeave = () => { this.paused = false; };
        this.next = this.next.bind(this);

        this.mq.addEventListener('change', this.onChange);
        this.addEventListener('mouseenter', this.onEnter);
        this.addEventListener('mouseleave', this.onLeave);
        this.addEventListener('focusin', this.onEnter);
        this.addEventListener('focusout', this.onLeave);

        this.reset();
        this.timer = setInterval(this.next, this.interval);
      }

      disconnectedCallback() {
        clearInterval(this.timer);
        this.mq.removeEventListener('change', this.onChange);
      }

      slides() {
        const view = this.mq.matches ? 'mobile' : 'desktop';
        return Array.from(this.querySelectorAll('[data-slide]')).filter(
          (el) => el.dataset.slide === 'all' || el.dataset.slide === view
        );
      }

      reset() {
        this.index = 0;
        this.show();
      }

      next() {
        if (this.paused || document.hidden) return;
        const count = this.slides().length;
        if (count < 2) return;
        this.index = (this.index + 1) % count;
        this.show();
      }

      show() {
        const active = this.slides()[this.index];
        this.querySelectorAll('[data-slide]').forEach((el) => {
          const on = el === active;
          el.classList.toggle('is-active', on);
          el.setAttribute('aria-hidden', on ? 'false' : 'true');
        });
        this.classList.toggle('is-static', this.reduced);
      }
    }

    customElements.define('cm-elixir-rotator', CmElixirRotator);
  }
})();
